import {
  isUint256,
  type AccountTransactionChildDraft,
} from '@mls-wallet/shared-types';
import { getAddress } from 'viem';

/** A child payload. Its source account and network are selected on the parent. */
export type RawTransaction = Pick<
  AccountTransactionChildDraft,
  'to' | 'value' | 'data'
>;

/** Normalize exact transaction bytes without trusting imported ABI metadata. */
export function normalizeRawTransaction(input: unknown): RawTransaction {
  // Case: each imported child must be a transaction object, not a nested batch or primitive.
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('Transaction JSON must be an object.');
  }
  const record = input as Record<string, unknown>;
  // Case: raw JSON may supply payload fields only; the builder owns account, network and policy.
  if (
    Object.keys(record).some((key) => !['to', 'value', 'data'].includes(key))
  ) {
    throw new Error('Transaction JSON may only contain to, value, and data.');
  }

  let to: string;
  // Case: reject invalid destinations before admitting the child into the draft.
  try {
    // Case: a missing or non-string destination cannot identify an EVM recipient.
    if (typeof record.to !== 'string') throw new Error();
    to = getAddress(record.to.trim());
  } catch {
    throw new Error('to must be a valid EVM address.');
  }
  // Case: require an integer string so JSON number rounding cannot change the amount.
  if (typeof record.value !== 'string' || !/^\d+$/.test(record.value.trim())) {
    throw new Error('value must be a non-negative base-unit integer string.');
  }
  const value = record.value.trim();
  // Case: amounts outside uint256 cannot be represented in the transaction payload.
  if (!isUint256(value)) {
    throw new Error('value must fit in an unsigned 256-bit integer.');
  }
  // Case: native sends use empty calldata; other payloads need a selector and complete bytes.
  if (
    typeof record.data !== 'string' ||
    !/^0x(?:[0-9a-fA-F]{8}(?:[0-9a-fA-F]{2})*)?$/.test(record.data.trim())
  ) {
    throw new Error(
      'data must be 0x for a native send, or byte-aligned calldata containing a 4-byte function selector.'
    );
  }
  return { to, value, data: record.data.trim() };
}
